"use client";

import { useEffect } from "react";
import { handleIfServerActionVersionSkew } from "@/utils/serverActions/versionSkew";

/**
 * Mounts global listeners that recover from Next.js server action "version skew"
 * errors (see {@link handleIfServerActionVersionSkew}).
 *
 * `useActionHandler` already handles skew for actions invoked through it. This
 * hook is the safety net for action invocations that bypass that path — e.g.
 * `<form action={...}>` submissions and `startTransition` calls — whose errors
 * surface as window `error` / `unhandledrejection` events instead.
 */
export function useServerActionRecovery(): void {
  useEffect(() => {
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (handleIfServerActionVersionSkew(event.reason, { source: "unhandledrejection" })) {
        event.preventDefault();
      }
    };

    const onError = (event: ErrorEvent) => {
      if (handleIfServerActionVersionSkew(event.error ?? event.message, { source: "error" })) {
        event.preventDefault();
      }
    };

    window.addEventListener("unhandledrejection", onUnhandledRejection);
    window.addEventListener("error", onError);

    return () => {
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
      window.removeEventListener("error", onError);
    };
  }, []);
}
